"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useObjectReview = void 0;
exports.useReviewDecisionStatusByObject = useReviewDecisionStatusByObject;
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const SurveyAdmin_1 = require("../../actions/SurveyAdmin");
const reviewDecisionStatusHelper_1 = require("./reviewDecisionStatusHelper");
function useReviewDecisionStatusByObject(objectType, objectUuid) {
    const isScoped = objectType !== undefined;
    return (0, react_redux_1.useSelector)((state) => {
        var _a;
        const reviewDecisionStatusByObject = (_a = state.survey.reviewDecisions) === null || _a === void 0 ? void 0 : _a.reviewDecisionStatusByObject;
        if (isScoped) {
            return (0, reviewDecisionStatusHelper_1.getReviewDecisionStatusForObject)(reviewDecisionStatusByObject, objectType, objectUuid);
        }
        return reviewDecisionStatusByObject;
    }, isScoped ? react_redux_1.shallowEqual : undefined);
}
/**
 * Hook exposing the review status and mutation callbacks for one survey object.
 * Reads from the `reviewDecisions` Redux slice and dispatches the review thunks,
 * so components only need the object type and uuid.
 * @param objectType - Survey object type key
 * @param objectUuid - Survey object uuid; controls are disabled when missing
 * @returns Review state and callbacks for the object
 */
const useObjectReview = (objectType, objectUuid) => {
    const dispatch = (0, react_redux_1.useDispatch)();
    const status = useReviewDecisionStatusByObject(objectType, objectUuid);
    const user = (0, react_redux_1.useSelector)((state) => { var _a; return (_a = state.auth) === null || _a === void 0 ? void 0 : _a.user; });
    const hasReviewControls = Boolean(objectUuid && (0, reviewDecisionStatusHelper_1.isReviewableObjectType)(objectType));
    const canForceApprove = Boolean(user === null || user === void 0 ? void 0 : user.isAuthorized({ Interviews: ['confirm'] }));
    const runWhenReviewable = (0, react_1.useCallback)((thunkFactory) => {
        if (hasReviewControls && objectUuid) {
            dispatch(thunkFactory(objectType, objectUuid));
        }
    }, [dispatch, objectType, objectUuid, hasReviewControls]);
    const approve = (0, react_1.useCallback)(() => runWhenReviewable((type, uuid) => (0, SurveyAdmin_1.startSubmitObjectReview)(type, uuid, 'approve')), [runWhenReviewable]);
    const reject = (0, react_1.useCallback)(() => runWhenReviewable((type, uuid) => (0, SurveyAdmin_1.startSubmitObjectReview)(type, uuid, 'reject')), [runWhenReviewable]);
    const clearReview = (0, react_1.useCallback)(() => runWhenReviewable(SurveyAdmin_1.startClearObjectReview), [runWhenReviewable]);
    const forceApprove = (0, react_1.useCallback)(() => runWhenReviewable(SurveyAdmin_1.startForceApproveObject), [runWhenReviewable]);
    const clearForceApprove = (0, react_1.useCallback)(() => runWhenReviewable(SurveyAdmin_1.startClearForceApproveObject), [runWhenReviewable]);
    const requestReReview = (0, react_1.useCallback)(() => runWhenReviewable(SurveyAdmin_1.startRequestReReview), [runWhenReviewable]);
    return (0, react_1.useMemo)(() => ({
        status,
        hasReviewControls,
        canForceApprove,
        approve,
        reject,
        clearReview,
        forceApprove,
        clearForceApprove,
        requestReReview
    }), [
        status,
        hasReviewControls,
        canForceApprove,
        approve,
        reject,
        clearReview,
        forceApprove,
        clearForceApprove,
        requestReReview
    ]);
};
exports.useObjectReview = useObjectReview;
//# sourceMappingURL=useObjectReview.js.map